import type {JSX} from 'react'
import {useEffect} from 'react'
import {safeDecodeURIComponent} from '@/utilities/text'
import {useWindowSize} from '@/utilities/hooks/use-window-size'

const normalizeAnchorValue = (value: string): string =>
	value.trim().replace(/\s+/g, '-').toLowerCase()

const normalizeHashFragment = (hash: string): string =>
	normalizeAnchorValue(safeDecodeURIComponent(hash.replace(/^#/, '')))

interface AnchorProps {
	anchorLink?: string
	classNames?: string
	scrollMarginTop?: {
		desktop: string
		mobile: string
	}
}

export const Anchor = ({
	anchorLink = '',
	classNames = '',
	scrollMarginTop = {
		desktop: '8rem',
		mobile: '4rem',
	},
}: AnchorProps): JSX.Element => {
	const size = useWindowSize()
	const {desktop, mobile} = scrollMarginTop
	const anchorId = normalizeAnchorValue(anchorLink)

	useEffect(() => {
		if (!anchorId) return

		const scrollToAnchor = (): void => {
			const hashId = normalizeHashFragment(
				globalThis.window.location.hash
			)
			if (!hashId) return
			if (hashId !== anchorId) return

			const target = document.getElementById(anchorId)
			if (!target) return
			target.scrollIntoView({behavior: 'smooth', block: 'start'})
		}

		const animationFrameId =
			globalThis.window.requestAnimationFrame(scrollToAnchor)

		return () => {
			globalThis.window.cancelAnimationFrame(animationFrameId)
		}
	}, [anchorId])

	return (
		<div
			className={`invisible h-0 w-0 ${classNames}`}
			id={anchorId}
			style={{scrollMarginTop: size.isMobile ? mobile : desktop}}
		/>
	)
}
